import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { constantsData, statusTypesData } from "@/data";
import { createDistributionMutation, getDistributionsListQuery } from "@/graph";
import { openAlert, toggleDistributionDrawer } from "@/redux/actions";
import { displayErrorHandler, normalizeDateToUTCMidnightHandler } from "@/utils";

const useCreateDistribution = (releaseId, hasRefetch, limit, page) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [createDistributionAction, { loading }] = useMutation(createDistributionMutation, {
        ...(hasRefetch && {
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: getDistributionsListQuery,
                    variables: {
                        limit,
                        page,
                        ...(releaseId && { releaseId }),
                    },
                },
            ],
        }),
    });

    const createDistribution = async ({
        availableForImmersiveDate,
        client,
        fileType,
        isApple,
        preOrderDate,
        preOrderType,
        releaseDate,
        salesStartDate,
        streamStartDate,
        territory,
        track,
        trackFormat,
    }) => {
        try {
            await createDistributionAction({
                onCompleted: () => {
                    dispatch(openAlert(t("messages.distributionMsgs.success"), statusTypesData.success));

                    dispatch(toggleDistributionDrawer(false));
                },
                variables: {
                    clientId: client,
                    distributionFileTypeId: fileType,
                    ...(releaseDate && !isApple && { releaseDate }),
                    releaseId,
                    territoryId: territory,
                    tracksFormat: trackFormat.map(({ value }) => value),
                    ...(isApple && {
                        extendedDetails: {
                            availableForImmersiveDate: normalizeDateToUTCMidnightHandler(availableForImmersiveDate),
                            salesStartDate: normalizeDateToUTCMidnightHandler(salesStartDate),
                            streamStartDate: normalizeDateToUTCMidnightHandler(streamStartDate),
                            ...(preOrderType &&
                                preOrderDate && {
                                    tracksPreOrder: {
                                        preOrderType,
                                        preorderSalesStartDate: normalizeDateToUTCMidnightHandler(preOrderDate),
                                        ...(track &&
                                            track?.length > 0 && { trackIds: track.map(({ value }) => value) }),
                                    },
                                }),
                        },
                    }),
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    return {
        createDistribution,
        loading,
    };
};

export default useCreateDistribution;
